// Real pedestrian routing. OSRM foot (FOSSGIS) first, Valhalla as a fallback,
// and if neither answers we never pretend: the caller shows "190 m away".

const OSRM = "https://routing.openstreetmap.de/routed-foot/route/v1/foot";
const VALHALLA = "https://valhalla1.openstreetmap.de/route";
const TIMEOUT_MS = 4500;

const cache = new Map();
const key = (a, b) =>
  `${a.lat.toFixed(4)},${a.lng.toFixed(4)}>${b.lat.toFixed(4)},${b.lng.toFixed(4)}`;

async function withTimeout(promise, ms) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  try {
    return await promise(controller.signal);
  } finally {
    clearTimeout(timer);
  }
}

async function osrmRoute(a, b) {
  const url = `${OSRM}/${a.lng},${a.lat};${b.lng},${b.lat}?overview=false&alternatives=false`;
  const res = await withTimeout((signal) => fetch(url, { signal }), TIMEOUT_MS);
  if (!res.ok) throw new Error("osrm");
  const data = await res.json();
  const r = (data.routes || [])[0];
  if (!r || !Number.isFinite(r.distance)) throw new Error("osrm_empty");
  return { distance_m: Math.round(r.distance), duration_s: Math.round(r.duration), engine: "osrm" };
}

async function valhallaRoute(a, b) {
  const body = {
    locations: [
      { lat: a.lat, lon: a.lng },
      { lat: b.lat, lon: b.lng },
    ],
    costing: "pedestrian",
    directions_options: { units: "kilometers" },
  };
  const res = await withTimeout(
    (signal) =>
      fetch(VALHALLA, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        signal,
      }),
    TIMEOUT_MS
  );
  if (!res.ok) throw new Error("valhalla");
  const data = await res.json();
  const s = data?.trip?.summary;
  if (!s || !Number.isFinite(s.length)) throw new Error("valhalla_empty");
  return {
    distance_m: Math.round(s.length * 1000),
    duration_s: Math.round(s.time),
    engine: "valhalla",
  };
}

/** `{distance_m, duration_s, engine}` for a real pedestrian route, or null. */
export async function walkingRoute(from, to) {
  if (!from || !to) return null;
  const k = key(from, to);
  if (cache.has(k)) return cache.get(k);
  let result = null;
  try {
    result = await osrmRoute(from, to);
  } catch {
    try {
      result = await valhallaRoute(from, to);
    } catch {
      result = null;
    }
  }
  cache.set(k, result);
  return result;
}

/** Resolves walking routes for the shortlist. Sequential with a small gap:
 *  the free routing services throttle bursts of parallel requests. */
export async function attachWalkingRoutes(destination, options, max = 3) {
  for (const o of options.slice(0, max)) {
    const r = await walkingRoute({ lat: o.lat, lng: o.lng }, destination);
    if (r) {
      o.walk_m = r.distance_m;
      o.walk_min = Math.max(1, Math.round(r.duration_s / 60));
    }
    await new Promise((resolve) => setTimeout(resolve, 200));
  }
  return options;
}
